import type { AlertEvent, AlertPolicy, AlertEntityType, AlertOperator } from "@/types/risk";

export const resolvePolicyId = (policy: Partial<AlertPolicy> & { policy_id?: unknown } | null | undefined): string | null => {
  if (!policy) return null;
  const raw = (policy as { id?: unknown }).id ?? (policy as { policy_id?: unknown }).policy_id;
  if (raw == null) return null;
  const s = String(raw);
  return s.length > 0 ? s : null;
};

export const formatDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : "-");

export const formatRelativeTime = (iso?: string | null): string | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Helper to safely parse possibly-JSON fields
const safeParse = (v: unknown): Record<string, unknown> | null => {
  if (v == null) return null;
  if (typeof v === "object") return v as Record<string, unknown>;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const toIso = (v: unknown): string | undefined => {
  if (typeof v === "string" && v.trim().length > 0) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return undefined;
};

export const mapAlertEvent = (row: unknown): AlertEvent => {
  const r = row as Record<string, unknown>;
  const observed = safeParse(r["observed"]);
  const operator = (r["operator"] as string) || (observed?.["operator"] as string) || undefined;
  const thresholdRaw = (r["threshold"] as number) ?? (observed?.["threshold"] as number);
  const valueRaw = (r["value"] as number) ?? (observed?.["value"] as number);
  const threshold = typeof thresholdRaw === "number" ? thresholdRaw : null;
  const value = typeof valueRaw === "number" ? valueRaw : null;

  const id = String((r["id"] as string | number | undefined) ?? (r["event_id"] as string | number | undefined) ?? "");
  const policyId = (r["policy_id"] as string | number | undefined);
  const entityType = (String(r["entity_type"] ?? "portfolio") as AlertEntityType);
  const op: AlertOperator | undefined = operator as AlertOperator | undefined;
  const statusRaw = (r["status"] as string) ?? null;
  const status = statusRaw === "open" || statusRaw === "ack" || statusRaw === "closed" ? statusRaw : null;

  return {
    id,
    policy_id: policyId != null ? String(policyId) : undefined,
    metric_key: String(r["metric_key"] ?? ""),
    entity_type: entityType,
    entity_id: (r["entity_id"] as string | number | null | undefined) != null ? String(r["entity_id"]) : null,
    operator: op,
    threshold,
    value,
    severity: (r["severity"] as string) ?? null,
    // Triggered at -> created_at only
    fired_at: toIso(r["created_at"]) ?? new Date().toISOString(),
    recovered_at: toIso(r["closed_at"]) ?? null,
    observed_at: toIso((observed?.["date"]) as string) ?? null,
    status,
    acked_at: toIso(r["acked_at"]) ?? null,
  } as AlertEvent;
};


